<template>
  <div>
    <div v-if="!embedded" class="title">
      <div class="title__right">
        <span class="back desktop" @click="$router.go(-1)">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="none" stroke="currentColor"
               stroke-width="48" stroke-linecap="round" stroke-linejoin="round">
            <path d="M328 112L184 256l144 144"/>
          </svg>
        </span>
        <h1>Contratos — {{ client.name }}</h1>
      </div>
      <div class="toolbar">
        <button @click="showNewSidebar = true" class="btn">+ Agregar</button>
      </div>
    </div>

    <ChipsBar
      :chips="[{label:'Todos',value:'all'},{label:'Activos',value:'active'},{label:'Vencidos',value:'expired'}]"
      v-model="filterStatus"
    />

    <div class="filter-bar filter-bar--no-top">
      <SortSelect :options="sortOptions" v-model="sortBy" />
    </div>

    <div class="dt-wrapper">
      <table class="dt" v-if="filteredList.length > 0">
        <thead>
          <tr>
            <th>Inicio</th>
            <th>Fin</th>
            <th>Renta</th>
            <th>Garantía</th>
            <th>Estado</th>
            <th aria-label="Acciones"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="o in displayList" :key="o.id">
            <td>{{ formatDate(o.start) }}</td>
            <td>{{ o.end ? formatDate(o.end) : 'Indefinido' }}</td>
            <td><span class="dt-cell__primary">{{ formatAmount(o.rent, o.currency) }}</span></td>
            <td>{{ formatAmount(o.warranty, o.currency) }}</td>
            <td>
              <span class="badge" :class="o.expired ? 'badge--error' : 'badge--success'">
                {{ o.expired ? 'Vencido' : 'Activo' }}
              </span>
            </td>
            <td>
              <div class="dt-actions">
                <span v-if="!o.expired" class="dt-icon" title="Editar"
                      @click="showUpdateSidebar = true; sidebarData = o">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </span>
                <span v-if="o.pdf" class="dt-icon" title="Ver PDF" @click="openPDF(o.pdf)">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </span>
                <span class="dt-icon dt-icon--danger" title="Eliminar" @click.stop="deleteContract(o)">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    <path d="M10 11v6"/><path d="M14 11v6"/>
                    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                  </svg>
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <EmptyState
        v-else
        title="Sin contratos"
        :message="filterStatus === 'all' ? 'No hay contratos registrados para este expositor.' : 'No hay contratos con ese estado.'"
      />
    </div>

    <NewContract :client="client" @close="showNewSidebar = false" @add="addSidebar"
                 :class="{'show': showNewSidebar}" title="Nuevo contrato" :show-navbar="showNewSidebar"/>
    <UpdateContract :data="sidebarData" @close="showUpdateSidebar = false" @update="updateSidebar"
                    :class="{'show': showUpdateSidebar}" title="Editar contrato" :show-navbar="showUpdateSidebar"/>
    <ConfirmDialog
      :show="showDeleteDialog"
      title="Eliminar contrato"
      message="¿Estás seguro de eliminar este contrato? Esta acción no se puede deshacer."
      confirm-text="Eliminar"
      cancel-text="Cancelar"
      variant="danger"
      :loading="deleting"
      @confirm="doDeleteContract"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script>
import { db } from '@/firebase/firebaseConfig'
import NewContract from './NewContract'
import UpdateContract from './UpdateContract'
import ConfirmDialog from '@/components/ConfirmDialog'
import ChipsBar from '@/components/ChipsBar'
import EmptyState from '@/components/EmptyState'
import SortSelect from '@/components/SortSelect'
import { sortItems } from '@/utils/sort'
import moment from 'moment'

export default {
  name: 'Contracts',
  props: {
    embedded: { type: Boolean, default: false }
  },
  components: { NewContract, UpdateContract, ConfirmDialog, ChipsBar, EmptyState, SortSelect },
  data () {
    return {
      list: [],
      client: { currency: {} },
      filterStatus: 'all',
      sortBy: 'start_desc',
      sortOptions: [
        { value: 'start_desc', label: 'Inicio (reciente primero)', field: 'start', type: 'date', dir: 'desc' },
        { value: 'start_asc', label: 'Inicio (antiguo primero)', field: 'start', type: 'date', dir: 'asc' },
        { value: 'rent_desc', label: 'Renta (mayor a menor)', field: 'rent', type: 'number', dir: 'desc' },
        { value: 'status', label: 'Estado (activos primero)', field: 'expired', type: 'bool', dir: 'asc' }
      ],
      showNewSidebar: false,
      showUpdateSidebar: false,
      sidebarData: {},
      showDeleteDialog: false,
      contractToDelete: null,
      deleting: false
    }
  },
  computed: {
    filteredList () {
      if (this.filterStatus === 'active') return this.list.filter(o => !o.expired)
      if (this.filterStatus === 'expired') return this.list.filter(o => o.expired)
      return this.list
    },
    displayList () {
      return sortItems(this.filteredList, this.sortOptions.find(o => o.value === this.sortBy))
    }
  },
  async created () {
    try {
      this.$store.dispatch('showProgress')
      await this.getClient()
      await this.getContracts()
    } catch (e) {
      this.$toast.error('Error al cargar contratos')
    } finally {
      this.$store.dispatch('hideProgress')
    }
  },
  methods: {
    formatDate (d) { return moment(d).locale('es').format('DD/MMM/YY') },
    formatAmount (amount, currency) {
      const code = currency && currency.code ? currency.code : 'PEN'
      return Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount || 0)
    },
    addSidebar (obj) { this.list.unshift(obj) },
    updateSidebar (o) {
      const idx = this.list.findIndex(d => d.id === o.id)
      if (idx !== -1) Object.assign(this.list[idx], o)
    },
    async getClient () {
      const doc = await db.collection('clients').doc(this.$route.params.id).get()
      if (doc.exists) this.client = { id: doc.id, ...doc.data() }
    },
    async getContracts () {
      const snap = await db.collection('clients').doc(this.client.id)
        .collection('contracts').orderBy('start', 'desc').get()
      snap.forEach(doc => {
        const obj = { id: doc.id, ...doc.data() }
        obj.start = doc.data().start.toDate()
        obj.end = doc.data().end ? doc.data().end.toDate() : null
        this.list.push(obj)
      })
    },
    openPDF (url) { window.open(url, '_blank') },
    deleteContract (contract) {
      this.contractToDelete = contract
      this.showDeleteDialog = true
    },
    cancelDelete () {
      if (this.deleting) return
      this.showDeleteDialog = false
      this.contractToDelete = null
    },
    async doDeleteContract () {
      if (!this.contractToDelete) return
      const contract = this.contractToDelete
      try {
        this.deleting = true
        await db.collection('clients').doc(this.client.id).collection('contracts').doc(contract.id).delete()
        this.list = this.list.filter(d => d.id !== contract.id)
        this.$toast.success('Contrato eliminado')
        this.showDeleteDialog = false
        this.contractToDelete = null
      } catch (e) {
        this.$toast.error('Error al eliminar contrato')
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>
